import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
} from '@mui/material';
import { Plus, Trash2, ArrowLeft, FileText, ListChecks, Sparkles, Upload } from 'lucide-react';
import { useAuthStore } from 'store/authStore';
import { createScenario, listRoles, listLlmModels } from 'services/scenariosApi';
import type { BackendRole, LlmModel } from 'services/scenariosApi';
import { uploadContextFile } from 'services/filesApi';
import { ContextDocumentTargetType as TargetType } from 'types';
import MarkdownEditor from 'components/MarkdownEditor';

const MAX_SCENARIO_FILES = 20;

interface FormState {
  title: string;
  shortDescription: string;
  roles: string[];
  steps: string[];
  result: string;
  instruction: string;
  system_prompt: string;
  model: string;
  minFiles: number;
  maxFiles: number;
}

const INITIAL: FormState = {
  title: '',
  shortDescription: '',
  roles: [],
  steps: [''],
  result: '',
  instruction: '',
  system_prompt: '',
  model: '',
  minFiles: 0,
  maxFiles: 5,
};

function validate(form: FormState): string | null {
  if (!form.title.trim()) return 'Укажите название';
  if (!form.shortDescription.trim()) return 'Укажите краткое описание';
  if (form.roles.length === 0) return 'Выберите хотя бы одну роль';
  if (form.steps.every((s) => !s.trim())) return 'Добавьте хотя бы один шаг';
  if (!form.result.trim()) return 'Укажите ожидаемый результат';
  if (!form.instruction.trim()) return 'Укажите инструкцию';
  if (!form.system_prompt.trim()) return 'Укажите системный промпт для AI';
  if (form.maxFiles < form.minFiles) return 'Макс. файлов не может быть меньше мин.';
  return null;
}

interface SectionProps {
  icon: React.ReactNode;
  title: string;
  description?: string;
  children: React.ReactNode;
}

function Section({ icon, title, description, children }: SectionProps) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, md: 3.5 },
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: (t) => `${t.palette.primary.main}14`,
            color: 'primary.main',
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.2px' }}>
            {title}
          </Typography>
          {description && (
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{description}</Typography>
          )}
        </Box>
      </Box>
      <Divider sx={{ my: 2.5 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>{children}</Box>
    </Paper>
  );
}

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <Typography
      sx={{
        fontSize: 12,
        fontWeight: 600,
        color: 'text.secondary',
        mb: 1,
        letterSpacing: 0.2,
      }}
    >
      {children}
      {required && ' *'}
    </Typography>
  );
}

export default function ScenarioCreatePage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const projectId = user?.projectId ?? '';
  const [form, setForm] = useState<FormState>(INITIAL);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [roles, setRoles] = useState<BackendRole[]>([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [models, setModels] = useState<LlmModel[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const scenarioFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    listRoles()
      .then((data) => setRoles((data ?? []).filter((r) => r.name !== 'admin')))
      .finally(() => setRolesLoading(false));
    listLlmModels().then((data) => setModels(data ?? []));
  }, []);

  const set = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const toggleRole = useCallback((roleName: string) => {
    setForm((prev) => ({
      ...prev,
      roles: prev.roles.includes(roleName)
        ? prev.roles.filter((r) => r !== roleName)
        : [...prev.roles, roleName],
    }));
  }, []);

  const setStep = useCallback((index: number, value: string) => {
    setForm((prev) => {
      const steps = [...prev.steps];
      steps[index] = value;
      return { ...prev, steps };
    });
  }, []);

  const addStep = useCallback(() => {
    setForm((prev) => ({ ...prev, steps: [...prev.steps, ''] }));
  }, []);

  const removeStep = useCallback((index: number) => {
    setForm((prev) => ({
      ...prev,
      steps: prev.steps.length > 1 ? prev.steps.filter((_, i) => i !== index) : prev.steps,
    }));
  }, []);

  const handleFilesSelected = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = '';
    if (files.length === 0) return;
    setPendingFiles((prev) => [...prev, ...files]);
  }, []);

  const removePendingFile = useCallback((index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSubmit = useCallback(async () => {
    const validationError = validate(form);
    if (validationError) {
      setError(validationError);
      return;
    }
    if (!user) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await createScenario({
        role_id: form.roles[0],
        title: form.title,
        description: form.shortDescription,
        action: form.steps.filter((s) => s.trim()).join('\n'),
        result: form.result,
        instruction: form.instruction,
        system_prompt: form.system_prompt,
        model: form.model || null,
        min_files: form.minFiles,
        max_files: form.maxFiles,
      });

      if (!result?.id) {
        setError('Не удалось создать сценарий. Попробуйте ещё раз.');
        return;
      }

      if (pendingFiles.length > 0 && projectId) {
        for (const file of pendingFiles) {
          const uploadResult = await uploadContextFile(
            projectId,
            { type: TargetType.Scenario, id: result.id },
            file
          );
          if (uploadResult.error) {
            setError(`Сценарий создан, но файл «${file.name}» не загружен: ${uploadResult.error}`);
            return;
          }
        }
      }

      navigate('/scenarios');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка создания сценария');
    } finally {
      setIsSubmitting(false);
    }
  }, [form, user, navigate, pendingFiles, projectId]);

  return (
    <Box sx={{ pb: 12, bgcolor: 'background.default', minHeight: '100%' }}>
      <Box
        sx={{
          p: { xs: 2, md: '32px 48px' },
          maxWidth: 1100,
          mx: 'auto',
          width: '100%',
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
          <IconButton
            onClick={() => navigate('/scenarios')}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              width: 40,
              height: 40,
            }}
          >
            <ArrowLeft size={18} />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontSize: 26,
                fontWeight: 800,
                letterSpacing: '-0.5px',
                color: 'text.primary',
                lineHeight: 1.2,
              }}
            >
              Новый сценарий
            </Typography>
            <Typography sx={{ fontSize: 14, color: 'text.secondary', mt: 0.5 }}>
              Создайте свой AI-сценарий
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Section 1: Basic Info */}
          <Section
            icon={<FileText size={18} />}
            title="Основная информация"
            description="Название, описание и кому доступен сценарий"
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2.5,
              }}
            >
              <TextField
                label="Название"
                value={form.title}
                onChange={(e) => set('title', e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Краткое описание"
                value={form.shortDescription}
                onChange={(e) => set('shortDescription', e.target.value)}
                fullWidth
                required
              />
            </Box>

            <Box>
              <FieldLabel required>Роли</FieldLabel>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {rolesLoading ? (
                  <CircularProgress size={18} />
                ) : (
                  roles.map((role) => {
                    const active = form.roles.includes(role.id);
                    return (
                      <Chip
                        key={role.id}
                        label={role.title ?? role.name}
                        onClick={() => toggleRole(role.id)}
                        color={active ? 'primary' : 'default'}
                        variant={active ? 'filled' : 'outlined'}
                        sx={{
                          cursor: 'pointer',
                          fontWeight: 500,
                          px: 0.5,
                          height: 32,
                        }}
                      />
                    );
                  })
                )}
              </Box>
            </Box>
          </Section>

          {/* Section 2: Scenario */}
          <Section
            icon={<ListChecks size={18} />}
            title="Сценарий"
            description="Что делает пользователь и какой ожидается результат"
          >
            <Box>
              <FieldLabel required>Шаги сценария</FieldLabel>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {form.steps.map((step, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        flexShrink: 0,
                        borderRadius: '50%',
                        bgcolor: (t) => `${t.palette.primary.main}14`,
                        color: 'primary.main',
                        fontSize: 12,
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {i + 1}
                    </Box>
                    <TextField
                      value={step}
                      onChange={(e) => setStep(i, e.target.value)}
                      placeholder={`Шаг ${i + 1}`}
                      fullWidth
                    />
                    <IconButton
                      onClick={() => removeStep(i)}
                      disabled={form.steps.length === 1}
                      sx={{
                        color: 'text.secondary',
                        '&:hover': { color: '#ef4444', bgcolor: '#ef444410' },
                      }}
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  startIcon={<Plus size={16} />}
                  onClick={addStep}
                  variant="outlined"
                  sx={{ alignSelf: 'flex-start', mt: 1, borderRadius: 2 }}
                >
                  Добавить шаг
                </Button>
              </Box>
            </Box>

            <TextField
              label="Ожидаемый результат"
              value={form.result}
              onChange={(e) => set('result', e.target.value)}
              fullWidth
              required
              multiline
              minRows={2}
            />

            <TextField
              label="Инструкция для пользователя"
              value={form.instruction}
              onChange={(e) => set('instruction', e.target.value)}
              fullWidth
              required
              multiline
              minRows={3}
              helperText="Подсказка пользователю — что нужно загрузить и как использовать сценарий"
            />
          </Section>

          {/* Section 3: AI */}
          <Section
            icon={<Sparkles size={18} />}
            title="Настройки AI"
            description="Промпт, модель и ограничения по файлам"
          >
            <MarkdownEditor
              label="Системный промпт для AI"
              value={form.system_prompt}
              onChange={(v) => set('system_prompt', v)}
              required
              height={360}
              helperText="Поддерживается Markdown: заголовки, списки, таблицы, код"
            />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' },
                gap: 2.5,
              }}
            >
              <FormControl fullWidth>
                <InputLabel shrink>Модель AI</InputLabel>
                <Select
                  value={form.model}
                  label="Модель AI"
                  onChange={(e) => set('model', e.target.value)}
                  displayEmpty
                  notched
                >
                  <MenuItem value="">
                    <em>По умолчанию</em>
                  </MenuItem>
                  {models.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Мин. файлов"
                type="number"
                value={form.minFiles}
                onChange={(e) =>
                  set('minFiles', Math.max(0, Math.min(MAX_SCENARIO_FILES, Number(e.target.value))))
                }
                inputProps={{ min: 0, max: MAX_SCENARIO_FILES }}
              />
              <TextField
                label="Макс. файлов"
                type="number"
                value={form.maxFiles}
                onChange={(e) =>
                  set('maxFiles', Math.max(1, Math.min(MAX_SCENARIO_FILES, Number(e.target.value))))
                }
                inputProps={{ min: 1, max: MAX_SCENARIO_FILES }}
              />
            </Box>

            {/* Scenario files subsection */}
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1,
                }}
              >
                <FieldLabel>Предзаданные файлы сценария</FieldLabel>
                <Box
                  onClick={() => scenarioFileInputRef.current?.click()}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.5,
                    py: 0.625,
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'primary.main',
                    bgcolor: (t) => `${t.palette.primary.main}14`,
                    border: '1px solid',
                    borderColor: (t) => `${t.palette.primary.main}40`,
                    borderRadius: 1.5,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    '&:hover': { bgcolor: (t) => `${t.palette.primary.main}22` },
                  }}
                >
                  <Upload size={13} />
                  Добавить файл
                </Box>
              </Box>
              <input
                ref={scenarioFileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                accept=".pdf,.docx,.doc,.txt,.xlsx,.csv"
                onChange={handleFilesSelected}
              />
              {pendingFiles.length === 0 ? (
                <Box
                  sx={{
                    border: '1.5px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    py: 2.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    color: 'text.disabled',
                    fontSize: 13,
                  }}
                >
                  <FileText size={16} />
                  Файлы не добавлены
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {pendingFiles.map((file, i) => (
                    <Box
                      key={`${file.name}-${i}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        px: 1.5,
                        py: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1.5,
                        bgcolor: 'background.default',
                      }}
                    >
                      <FileText size={15} style={{ flexShrink: 0, color: '#6b7280' }} />
                      <Typography
                        sx={{
                          flex: 1,
                          fontSize: 13,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: 'text.primary',
                        }}
                      >
                        {file.name}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: 'text.disabled', flexShrink: 0 }}>
                        {Math.round(file.size / 1024)} КБ
                      </Typography>
                      <Box
                        onClick={() => !isSubmitting && removePendingFile(i)}
                        title="Убрать"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 28,
                          height: 28,
                          borderRadius: 1,
                          flexShrink: 0,
                          cursor: isSubmitting ? 'not-allowed' : 'pointer',
                          color: 'text.secondary',
                          '&:hover': isSubmitting ? {} : { bgcolor: '#ef444410', color: '#ef4444' },
                          transition: 'all 0.12s',
                        }}
                      >
                        <Trash2 size={14} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Section>
        </Box>
      </Box>

      {/* Sticky Footer */}
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          py: 2,
          px: { xs: 2, md: 6 },
          backdropFilter: 'blur(8px)',
          zIndex: 10,
        }}
      >
        <Box
          sx={{
            maxWidth: 1100,
            mx: 'auto',
            display: 'flex',
            gap: 1.5,
            alignItems: 'center',
          }}
        >
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
            startIcon={
              isSubmitting ? <CircularProgress size={14} color="inherit" /> : <Plus size={16} />
            }
            sx={{ minWidth: 200, py: 1.1, borderRadius: 2, fontWeight: 600 }}
          >
            {isSubmitting ? 'Создание...' : 'Создать сценарий'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/scenarios')}
            disabled={isSubmitting}
            sx={{ borderRadius: 2 }}
          >
            Отмена
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
